import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { User, Package, LogOut, Award } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Profile() {
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    fetchOrders();
  }, [user, navigate]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`);
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50 py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold mb-8" style={{ fontFamily: 'Cormorant Garamond, serif' }} data-testid="profile-title">
          Личный кабинет
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* User Info */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-6 shadow-lg" data-testid="user-info">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center">
                  <User className="w-8 h-8 text-sky-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">{user.name}</h2>
                  <p className="text-gray-600 text-sm">{user.email}</p>
                </div>
              </div>

              {user.phone && (
                <div className="mb-6 text-gray-600">
                  <strong>Телефон:</strong> {user.phone}
                </div>
              )}

              <div className="bg-gradient-to-r from-sky-50 to-cyan-50 rounded-xl p-4 mb-6">
                <div className="flex items-center space-x-3">
                  <Award className="w-6 h-6 text-sky-600" />
                  <div>
                    <p className="text-sm text-gray-600">Бонусные баллы</p>
                    <p className="text-2xl font-bold text-sky-600" data-testid="bonus-points">{user.bonus_points}</p>
                  </div>
                </div>
              </div>

              <Button
                onClick={logout}
                data-testid="profile-logout"
                variant="outline"
                className="w-full"
              >
                <LogOut className="w-5 h-5 mr-2" />
                Выйти
              </Button>
            </div>
          </div>

          {/* Orders */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <div className="flex items-center space-x-3 mb-6">
                <Package className="w-6 h-6 text-sky-600" />
                <h2 className="text-2xl font-bold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  История заказов
                </h2>
              </div>

              {orders.length > 0 ? (
                <div className="space-y-4" data-testid="orders-list">
                  {orders.map(order => (
                    <div key={order.id} className="border rounded-xl p-6 hover:shadow-md transition-shadow" data-testid={`order-${order.id}`}>
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="font-bold text-lg">Заказ №{order.id.slice(0, 8)}</h3>
                          <p className="text-sm text-gray-600">
                            {new Date(order.created_at).toLocaleDateString('ru-RU', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </p>
                        </div>
                        <span className="px-3 py-1 bg-sky-100 text-sky-700 rounded-full text-sm font-medium">
                          {order.status}
                        </span>
                      </div>
                      <div className="space-y-2">
                        {order.items.map((item, idx) => (
                          <div key={idx} className="flex justify-between text-sm text-gray-600">
                            <span>{item.product_name} x{item.quantity}</span>
                            <span className="font-medium">{item.subtotal.toLocaleString('ru-RU')} ₽</span>
                          </div>
                        ))}
                      </div>
                      <div className="mt-4 pt-4 border-t flex justify-between items-center">
                        <span className="font-semibold">Итого:</span>
                        <span className="text-xl font-bold text-sky-600">{order.final_total.toLocaleString('ru-RU')} ₽</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12" data-testid="no-orders">
                  <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">У вас пока нет заказов</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}